import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, RotateCcw, ExternalLink, GraduationCap, Plus, Trash2, Loader2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { logger } from '../../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyReceive = () => ({ title: '', description: '' });

const FIELD_META = {
  brand_name:            { label: 'Brand name',        placeholder: 'PAWSOME VENTURES' },
  brand_tagline:         { label: 'Brand tagline',     placeholder: 'Making stress-free pet travel pawsible' },
  title:                 { label: 'Main title',        placeholder: 'Pet Travel Essentials' },
  subtitle:              { label: 'Subtitle',          placeholder: 'Front Desk Training for Veterinary Teams' },
  hero_footer:           { label: 'Hero tagline',      placeholder: 'Practical. Relevant.' },
  pill_label:            { label: 'Pill label',        placeholder: '60-MIN IN-PERSON' },
  pill_supp:             { label: 'Pill supp. text',   placeholder: '· Reception · Client-care · Front-of-house' },
  intro:                 { label: 'Intro paragraph',   placeholder: 'International pet travel is complex…', multiline: true, rows: 4 },
  learn_title:           { label: 'Left column title', placeholder: 'Your team will learn' },
  receive_title:         { label: 'Right column title', placeholder: 'Your team will also receive' },
  investment_title:      { label: 'Investment title',  placeholder: 'INVESTMENT' },
  investment_subtitle:   { label: 'Investment subtitle', placeholder: '· two ways to book this training' },
  tier_standard_label:   { label: 'Tier 1 label',      placeholder: 'STANDARD CLINIC' },
  tier_standard_price:   { label: 'Tier 1 price',      placeholder: 'CI$300' },
  tier_standard_sub:     { label: 'Tier 1 description', placeholder: '60-minute in-person session…', multiline: true, rows: 2 },
  tier_partner_label:    { label: 'Tier 2 label',      placeholder: 'PAWSOME TRUSTED PARTNER' },
  tier_partner_price:    { label: 'Tier 2 price',      placeholder: 'CI$225' },
  tier_partner_sub:      { label: 'Tier 2 description', placeholder: 'Preferential rate…', multiline: true, rows: 2 },
  partner_privileges_title: { label: 'Partner benefits heading', placeholder: 'Trusted partner privileges' },
  partner_privileges:    { label: 'Partner benefits copy', placeholder: 'Partner clinics enjoy…', multiline: true, rows: 3 },
  footer_trainer_name:   { label: 'Trainer line',      placeholder: 'Delivered by Dr. Samantha Dorman, MSc, BVSc' },
  footer_trainer_role:   { label: 'Trainer role',      placeholder: 'Founder · Veterinarian…' },
  footer_trainer_bio:    { label: 'Trainer bio',       placeholder: 'Specialist experience…', multiline: true, rows: 2 },
  footer_website:        { label: 'Website',           placeholder: 'pawsomeventures.ky' },
  footer_email:          { label: 'Contact email',     placeholder: 'pawsome.ky@gmail.com' },
  footer_phone:          { label: 'Contact phone',     placeholder: '+1 345 916 2281' },
};

// Groups shown as accordion-free sections on the editor.
const SECTIONS = [
  { title: 'Header',      keys: ['brand_name', 'brand_tagline', 'title', 'subtitle', 'hero_footer', 'pill_label', 'pill_supp'] },
  { title: 'Intro',       keys: ['intro'] },
  { title: 'Column headings', keys: ['learn_title', 'receive_title'] },
  { title: 'Investment box', keys: ['investment_title', 'investment_subtitle', 'tier_standard_label', 'tier_standard_price', 'tier_standard_sub', 'tier_partner_label', 'tier_partner_price', 'tier_partner_sub'] },
  { title: 'Trusted partner benefits', keys: ['partner_privileges_title', 'partner_privileges'] },
  { title: 'Footer',      keys: ['footer_trainer_name', 'footer_trainer_role', 'footer_trainer_bio', 'footer_website', 'footer_email', 'footer_phone'] },
];

export const VetTrainingFlyerEditor = () => {
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');
  const auth = { Authorization: `Basic ${btoa(':' + password)}` };

  const [content, setContent] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [previewKey, setPreviewKey] = useState(0);   // cache-buster for the iframe

  useEffect(() => {
    if (!password) { navigate('/admin/login'); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/settings/vet-training-flyer`, { headers: auth });
      setContent(data.content);
      setDefaults(data.defaults);
    } catch (e) {
      logger.error('flyer content load', e);
      toast.error('Failed to load flyer content');
    } finally { setLoading(false); }
  };

  const setField = (k, v) => setContent((prev) => ({ ...prev, [k]: v }));
  const setLearnItem = (i, v) => setContent((prev) => {
    const items = [...(prev.learn_items || [])];
    items[i] = v;
    return { ...prev, learn_items: items };
  });
  const addLearnItem = () => setContent((prev) => ({ ...prev, learn_items: [...(prev.learn_items || []), ''] }));
  const removeLearnItem = (i) => setContent((prev) => ({ ...prev, learn_items: prev.learn_items.filter((_, idx) => idx !== i) }));

  const setReceiveItem = (i, key, v) => setContent((prev) => {
    const items = [...(prev.receive_items || [])];
    items[i] = { ...items[i], [key]: v };
    return { ...prev, receive_items: items };
  });
  const addReceiveItem = () => setContent((prev) => ({ ...prev, receive_items: [...(prev.receive_items || []), emptyReceive()] }));
  const removeReceiveItem = (i) => setContent((prev) => ({ ...prev, receive_items: prev.receive_items.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings/vet-training-flyer`, content, { headers: auth });
      toast.success('Flyer saved — preview refreshed');
      setPreviewKey((k) => k + 1);
    } catch (e) {
      logger.error('flyer save', e);
      toast.error('Save failed');
    } finally { setSaving(false); }
  };

  const resetAll = async () => {
    if (!window.confirm('Reset the flyer to the original wording? Your current edits will be discarded.')) return;
    try {
      const { data } = await axios.post(`${API}/admin/settings/vet-training-flyer/reset`, {}, { headers: auth });
      setContent(data.content);
      setPreviewKey((k) => k + 1);
      toast.success('Reverted to defaults');
    } catch (e) { toast.error('Reset failed'); }
  };

  const previewUrl = useMemo(
    () => `${process.env.REACT_APP_BACKEND_URL}/api/brand/vet-training-flyer.pdf?v=${previewKey}`,
    [previewKey],
  );

  if (loading || !content) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-foreground/60">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading flyer content…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="flyer-editor">
      {/* Header */}
      <div className="bg-white border-b border-border sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin/settings')} className="p-2 rounded-lg hover:bg-gray-100">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h1 className="text-base sm:text-xl font-bold text-primary">Vet Training Flyer</h1>
              <p className="text-xs text-foreground/60">Edit any word or price · preview refreshes on save</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-border rounded-lg hover:bg-gray-50" data-testid="flyer-open-pdf">
              <ExternalLink className="w-4 h-4" /> Open PDF
            </a>
            <Button variant="outline" onClick={resetAll} data-testid="flyer-reset">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Reset
            </Button>
            <Button onClick={save} disabled={saving} data-testid="flyer-save">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1.5" /> Save</>}
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Form */}
        <div className="lg:col-span-3 space-y-6">
          {SECTIONS.map((section) => (
            <div key={section.title} className="bg-white border border-border rounded-xl p-5" data-testid={`section-${section.title.replace(/[^a-z]/gi, '-').toLowerCase()}`}>
              <h2 className="text-sm font-bold uppercase tracking-wide text-primary mb-3">{section.title}</h2>
              <div className="space-y-3">
                {section.keys.map((k) => {
                  const meta = FIELD_META[k];
                  if (!meta) return null;
                  return (
                    <div key={k}>
                      <Label>{meta.label}</Label>
                      {meta.multiline ? (
                        <textarea
                          value={content[k] || ''}
                          onChange={(e) => setField(k, e.target.value)}
                          rows={meta.rows || 3}
                          data-testid={`field-${k}`}
                          className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder={meta.placeholder}
                        />
                      ) : (
                        <Input
                          value={content[k] || ''}
                          onChange={(e) => setField(k, e.target.value)}
                          placeholder={meta.placeholder}
                          data-testid={`field-${k}`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Learn items (left bullet list) */}
          <div className="bg-white border border-border rounded-xl p-5" data-testid="section-learn-items">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold uppercase tracking-wide text-primary">Left bullet list</h2>
              <Button variant="outline" size="sm" onClick={addLearnItem} data-testid="add-learn-item">
                <Plus className="w-4 h-4 mr-1" /> Add bullet
              </Button>
            </div>
            <p className="text-xs text-foreground/50 mb-3">Up to 10 bullets appear on the flyer.</p>
            <div className="space-y-2">
              {(content.learn_items || []).map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-5 text-xs font-mono text-foreground/40">{i + 1}.</span>
                  <Input
                    value={item}
                    onChange={(e) => setLearnItem(i, e.target.value)}
                    data-testid={`learn-item-${i}`}
                    className="flex-1"
                  />
                  <button onClick={() => removeLearnItem(i)} className="p-1.5 rounded hover:bg-red-50 text-red-600" data-testid={`learn-remove-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Receive items (right takeaway cards) */}
          <div className="bg-white border border-border rounded-xl p-5" data-testid="section-receive-items">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold uppercase tracking-wide text-primary">Right takeaway cards</h2>
              <Button variant="outline" size="sm" onClick={addReceiveItem} data-testid="add-receive-item">
                <Plus className="w-4 h-4 mr-1" /> Add card
              </Button>
            </div>
            <p className="text-xs text-foreground/50 mb-3">Up to 4 cards fit on the flyer.</p>
            <div className="space-y-3">
              {(content.receive_items || []).map((it, i) => (
                <div key={i} className="border border-border rounded-lg p-3 space-y-2 bg-gray-50/50">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-foreground/60 uppercase">Card {i + 1}</span>
                    <button onClick={() => removeReceiveItem(i)} className="p-1 rounded hover:bg-red-50 text-red-600" data-testid={`receive-remove-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <Input
                    value={it.title || ''}
                    placeholder="Card title"
                    onChange={(e) => setReceiveItem(i, 'title', e.target.value)}
                    data-testid={`receive-item-${i}-title`}
                  />
                  <textarea
                    value={it.description || ''}
                    placeholder="Card description"
                    onChange={(e) => setReceiveItem(i, 'description', e.target.value)}
                    rows={2}
                    data-testid={`receive-item-${i}-description`}
                    className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Live preview */}
        <div className="lg:col-span-2 space-y-3">
          <div className="bg-white border border-border rounded-xl p-3 sticky top-24">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-foreground/60 uppercase">Live preview</span>
              <span className="text-[11px] text-foreground/40">Refreshes on Save</span>
            </div>
            <div className="aspect-[210/297] bg-gray-100 rounded-lg overflow-hidden border border-border">
              <iframe
                key={previewKey}
                title="Flyer preview"
                src={previewUrl}
                className="w-full h-full"
                data-testid="flyer-preview-iframe"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VetTrainingFlyerEditor;
